import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const LISTING_API_URL = "https://funs6nk6j9.execute-api.ap-south-1.amazonaws.com/Production";
const COMPANY_LOGO = "/assets/img/Website_logo_color logo-black text-1.png";
const PLAY_STORE_URL =
  "https://play.google.com/store/search?q=Instatenders&c=apps&hl=en_IN";
const CONTACT_EMAIL = "info@instatenders.in";
const PAGE_LIMIT = 27;

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

function getCategoryName(category) {
  if (!category) return "";
  if (typeof category === "string") return category.trim();
  if (typeof category === "object") {
    return String(category.category || category.name || "").trim();
  }
  return "";
}

function formatCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "";
  return `₹${num.toLocaleString("en-IN")}`;
}

function getSupportPhones(supportContact) {
  return String(supportContact || "")
    .split(/[/,]/)
    .map((part) => part.trim())
    .filter((part) => /^\d{6,}$/.test(part.replace(/\s+/g, "")));
}

function getDueState(dueDateTime) {
  if (!dueDateTime) return null;
  const due = new Date(dueDateTime);
  if (Number.isNaN(due.getTime())) return null;

  const now = Date.now();
  const diffMs = due.getTime() - now;
  if (diffMs <= 0) {
    return { type: "closed", label: "Closed" };
  }

  const diffHours = diffMs / (1000 * 60 * 60);
  if (diffHours <= 48) {
    const days = Math.floor(diffHours / 24);
    const hours = Math.round(diffHours % 24);
    const label =
      days > 0
        ? `Closing in ${days}d ${hours}h`
        : `Closing in ${Math.max(1, hours)}h`;
    return { type: "closing", label };
  }

  const diffDays = Math.ceil(diffHours / 24);
  return { type: "open", label: `${diffDays} days left` };
}

const LeadCardSkeleton = () => (
  <div className="lead-card lead-card-skeleton" aria-hidden="true">
    <div className="lead-skel lead-skel-badge" />
    <div className="lead-skel lead-skel-title" />
    <div className="lead-skel lead-skel-line" />
    <div className="lead-skel lead-skel-line short" />
    <div className="lead-skel lead-skel-footer" />
  </div>
);

const ListingLeads = () => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const searchDebounceRef = useRef(null);

  const fetchLeads = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const response = await axios.post(LISTING_API_URL, {
        action: "getAllContent",
        page: currentPage,
        limit: PAGE_LIMIT,
        search: activeSearch,
        category: "",
        rfqNo: "",
        status: "",
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      const responseData = parseApiResponse(response.data);
      const list = Array.isArray(responseData?.data) ? responseData.data : [];
      const pagination = responseData?.pagination || {};

      setLeads(list);
      setTotalPages(Number(pagination.totalPages) || 1);
      setTotalRecords(Number(pagination.totalRecords) || list.length);
    } catch (error) {
      console.error("Fetch listings error:", error);
      setLeads([]);
      setTotalPages(1);
      setTotalRecords(0);
      setLoadError(
        "Unable to load listings right now. Please try again in a moment.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, activeSearch]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Debounced live search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setActiveSearch(value.trim());
      setCurrentPage(1);
    }, 450);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      setActiveSearch(searchInput.trim());
      setCurrentPage(1);
    }
  };

  const handleClearSearch = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    setSearchInput("");
    setActiveSearch("");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    const next = Math.min(Math.max(1, page), totalPages);
    if (next !== currentPage) {
      setCurrentPage(next);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
      return pages;
    }

    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      start = 2;
      end = 4;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 3;
      end = totalPages - 1;
    }

    if (start > 2) pages.push("ellipsis-a");
    for (let i = start; i <= end; i += 1) pages.push(i);
    if (end < totalPages - 1) pages.push("ellipsis-b");
    pages.push(totalPages);
    return pages;
  };

  const buildContactHref = (lead) =>
    `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      `Interested in ${lead.rfqNo || "your listing"}`,
    )}&body=${encodeURIComponent(
      `Hi InstaTenders team,\n\nI am interested in the requirement "${
        lead.requirementSummary || ""
      }" (${lead.rfqNo || ""}). Please contact me with more details.\n\nThanks`,
    )}`;

  const currentYear = new Date().getFullYear();
  const startItem = totalRecords === 0 ? 0 : (currentPage - 1) * PAGE_LIMIT + 1;
  const endItem = Math.min(currentPage * PAGE_LIMIT, totalRecords);

  return (
    <div className="auth-shell accent-custom">
      <header className="auth-header">
        <div className="auth-header-inner">
          <Link to="/" className="auth-brand" aria-label="InstaTenders Home">
            <img src={COMPANY_LOGO} alt="InstaTenders" />
          </Link>

          <nav className="auth-nav">
          <Link to="/po-tracking" className="auth-nav-link">
              Track Purchase Order
            </Link>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="auth-store-btn"
            >
              <i className="fa-brands fa-google-play" aria-hidden="true" />
              Android
            </a>
            <Link to="/signin" className="auth-store-btn leads-signin-btn">
              <i className="fa-solid fa-right-to-bracket" aria-hidden="true" />
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      <main className="leads-main">
        <section className="leads-hero">
          <div className="leads-hero-inner">
            <span className="auth-eyebrow">
              <i className="fa-solid fa-bullhorn" /> Live Procurement Leads
            </span>
            <h1>
              Active requirements from <span>real buyers</span>
            </h1>
            <p>
              Browse the latest RFQs published on InstaTenders. Found a
              requirement that matches your business? Contact our team and
              start quoting.
            </p>

            <div className="leads-search-wrap">
              <div className="leads-search">
                <i className="fa-solid fa-magnifying-glass" />
                <input
                  type="text"
                  placeholder="Search by RFQ number or requirement..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  aria-label="Search listings"
                />
                {searchInput && (
                  <button
                    type="button"
                    className="leads-search-clear"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                )}
              </div>
              <div className="leads-count">
                {isLoading ? (
                  "Loading listings..."
                ) : totalRecords > 0 ? (
                  <>
                    Showing <strong>{startItem}–{endItem}</strong> of{" "}
                    <strong>{totalRecords.toLocaleString("en-IN")}</strong>{" "}
                    listings
                  </>
                ) : (
                  "No listings found"
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="leads-list-section">
          <div className="leads-list-inner">
            {isLoading ? (
              <div className="leads-grid">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <LeadCardSkeleton key={idx} />
                ))}
              </div>
            ) : loadError ? (
              <div className="leads-empty">
                <i className="fa-solid fa-triangle-exclamation" />
                <p>{loadError}</p>
                <button
                  type="button"
                  className="auth-submit-btn leads-retry-btn"
                  onClick={fetchLeads}
                >
                  Try again
                </button>
              </div>
            ) : leads.length === 0 ? (
              <div className="leads-empty">
                <i className="fa-solid fa-box-open" />
                <p>
                  No listings match{" "}
                  {activeSearch ? <strong>“{activeSearch}”</strong> : "your search"}.
                </p>
                {activeSearch && (
                  <button
                    type="button"
                    className="auth-submit-btn leads-retry-btn"
                    onClick={handleClearSearch}
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="leads-grid">
                  {leads.map((lead) => {
                    const dueState = getDueState(lead.rfqDueDateTime);
                    const categoryName = getCategoryName(lead.category);
                    const summaryText = String(lead.summary || "").trim();
                    const supportPhones = getSupportPhones(lead.supportContact);
                    const emdAmount = lead.emdVisible
                      ? formatCurrency(lead.emdPayment)
                      : "";
                    const tenderFeeAmount = lead.tenderFeeVisible
                      ? formatCurrency(lead.tenderFee)
                      : "";

                    return (
                      <article className="lead-card" key={lead._id}>
                        <div className="lead-card-top">
                          <span className="lead-rfq-no">
                            <i className="fa-solid fa-hashtag" />
                            {lead.rfqNo || "RFQ"}
                          </span>
                          {dueState && (
                            <span className={`lead-due-badge lead-due-${dueState.type}`}>
                              {dueState.type === "closing" && (
                                <i className="fa-solid fa-hourglass-half" />
                              )}
                              {dueState.type === "open" && (
                                <i className="fa-regular fa-clock" />
                              )}
                              {dueState.type === "closed" && (
                                <i className="fa-solid fa-lock" />
                              )}
                              {dueState.label}
                            </span>
                          )}
                        </div>

                        <h3 className="lead-title">
                          {lead.requirementSummary || "Procurement requirement"}
                        </h3>

                        {summaryText &&
                          summaryText !== lead.requirementSummary && (
                            <p className="lead-summary">{summaryText}</p>
                          )}

                        <div className="lead-meta">
                          <div className="lead-meta-item">
                            <i className="fa-regular fa-calendar" />
                            <span>
                              <small>RFQ Date</small>
                              {formatDate(lead.rfqDate)}
                            </span>
                          </div>
                          <div className="lead-meta-item">
                            <i className="fa-regular fa-calendar-check" />
                            <span>
                              <small>Due By</small>
                              {formatDateTime(lead.rfqDueDateTime)}
                            </span>
                          </div>
                        </div>

                        {(categoryName || emdAmount || tenderFeeAmount) && (
                          <div className="lead-tags">
                            {categoryName && (
                              <span className="lead-tag lead-tag-category">
                                <i className="fa-solid fa-tag" />
                                {categoryName}
                              </span>
                            )}
                            {emdAmount && (
                              <span className="lead-tag lead-tag-fee">
                                EMD: {emdAmount}
                              </span>
                            )}
                            {tenderFeeAmount && (
                              <span className="lead-tag lead-tag-fee">
                                Tender Fee: {tenderFeeAmount}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="lead-card-footer">
                          {supportPhones.length > 0 ? (
                            <a
                              href={`tel:${supportPhones[0]}`}
                              className="lead-contact-btn"
                            >
                              <i className="fa-solid fa-phone" />
                              {supportPhones[0]}
                            </a>
                          ) : (
                            <a
                              href={buildContactHref(lead)}
                              className="lead-contact-btn"
                            >
                              <i className="fa-solid fa-envelope" />
                              Contact Team
                            </a>
                          )}
                          
                        </div>
                      </article>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <nav className="leads-pagination" aria-label="Listings pages">
                    <button
                      type="button"
                      className="leads-page-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      <i className="fa-solid fa-chevron-left" />
                    </button>
                    {getPageNumbers().map((page) =>
                      typeof page === "number" ? (
                        <button
                          key={page}
                          type="button"
                          className={`leads-page-btn${
                            page === currentPage ? " active" : ""
                          }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ) : (
                        <span key={page} className="leads-page-ellipsis">
                          …
                        </span>
                      ),
                    )}
                    <button
                      type="button"
                      className="leads-page-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                    >
                      <i className="fa-solid fa-chevron-right" />
                    </button>
                  </nav>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <footer className="auth-footer leads-footer">
        <div className="auth-footer-bottom">
          <p>{currentYear} © InstaTenders Solutions Pvt Ltd, All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ListingLeads;
